'use client';

import { cloneElement, type JSX } from 'react';
import {
  Input,
  Select,
  SelectSection,
  SelectItem,
  Textarea,
  Tooltip,
} from "@heroui/react";
import { HTMLInputTypeAttribute } from 'react';
import { Controller } from 'react-hook-form';
import { BiCheck } from 'react-icons/bi';

// import { Form, FloatingLabel, OverlayTrigger, Tooltip } from 'react-bootstrap';

type SelectObject = {
  value: string | number;
  label: string | JSX.Element;
  textValue?: string;
  disabled?: boolean;
};

type InputFloatingLabelProps = {
  label?: string;
  type?: HTMLInputTypeAttribute;
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  focusHandler?: (e: React.FocusEvent<HTMLInputElement>) => void;
  blurHandler?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputStyle?: React.CSSProperties;
  children?: React.ReactNode;
  isValid?: boolean;
  register?: any;
  error?: any;
  placeholder?: string;
  value?: any;
  changeHandler?: any;
  disabled?: boolean;
  options?: SelectObject[];
  sectionOptions?: {
    sectionLabel: string;
    options: SelectObject[];
  }[];
  optionsLabel?: string;
  handleOpenSelect?: any;
  endContent?: React.ReactNode;
  startContent?: React.ReactNode;
  isArea?: boolean;
  size?: string;
  fullWidth?: boolean;
  isClearable?: boolean;
  onClear?: any;
  labelPlacement?: 'inside' | 'outside' | 'outside-left';
  control?: any;
  controlName?: string;
  rules?: any;
  as?: string;
  selectionMode?: 'single' | 'multiple';
  min?: number | string;
  max?: number | string;
  step?: number | string;
  isRequired?: boolean;
};

const InputFloatingLabel = ({
  label,
  type = 'text',
  // isArea = false,
  tooltip,
  tooltipPlacement = 'bottom',
  focusHandler,
  blurHandler,
  inputStyle,
  children,
  register,
  error,
  placeholder = ' ',
  value,
  changeHandler,
  disabled = false,
  options,
  optionsLabel,
  sectionOptions,
  handleOpenSelect,
  isValid = false,
  endContent,
  startContent,
  isArea,
  size,
  fullWidth = true,
  isClearable,
  onClear,
  labelPlacement = 'inside',
  control,
  controlName,
  rules,
  as,
  selectionMode = 'single',
  min,
  max,
  step,
  isRequired,
}: InputFloatingLabelProps) => {
  const baseProps = {
    type,
    label,
    placeholder,
    'aria-label': label || placeholder || 'Enter Value',
    autoComplete: 'off',
    style: inputStyle,
    isInvalid: !!error,
    isValid,
    errorMessage: error?.message || error,
    isDisabled: disabled,
    variant: 'bordered',
    startContent,
    size,
    labelPlacement,
    as,
    isRequired,
  };

  const controlledProps = {
    value,
    onChange: changeHandler,
  };

  const inputComp = (
    <Input
      {...(value === undefined && register)}
      {...(changeHandler && controlledProps)}
      {...baseProps}
      validationState={!!error ? 'invalid' : 'valid'}
      // isInvalid={!!error}
      onFocus={focusHandler}
      onBlur={blurHandler}
      classNames={{
        inputWrapper: 'bg-white border shadow-none',
        label: 'font-light uppercase text-xs',
        input: 'text-base md:text-sm',
      }}
      endContent={
        isValid ? <BiCheck size={30} className='text-success' /> : endContent
      }
      isClearable={isClearable}
      onClear={onClear}
      step={step ?? 'any'}
      min={min}
      max={max}
    />
  );

  const area = (
    <Textarea
      {...(!value && register)}
      {...(!!changeHandler && controlledProps)}
      {...baseProps}
      classNames={{
        inputWrapper: 'bg-white border shadow-none',
        label: 'font-light uppercase text-xs',
        input: 'text-base md:text-sm',
      }}
      labelPlacement='inside'
      endContent={
        isValid ? <BiCheck size={30} className='text-success' /> : endContent
      }
    />
  );

  const controlledText =
    !!control && controlName ? (
      <Controller
        control={control}
        name={controlName}
        rules={rules}
        render={({ field: { value, onChange }, fieldState: { error: fieldError } }) =>
          cloneElement(!isArea ? inputComp : area, {
            value,
            onChange,
            // Use fieldState error if available, otherwise use prop error
            errorMessage: fieldError?.message || fieldError || error?.message || error,
            isInvalid: !!fieldError || !!error,
            validationState: (fieldError || error) ? 'invalid' : 'valid',
          })
        }
      />
    ) : null;

  const createSelectItems = (options: SelectObject[]) => {
    return options.map((option) => (
      <SelectItem
        key={option.value}
        // value={option.value}
        textValue={option.textValue}
      >
        {option.label}
      </SelectItem>
    ));
  };

  const select = (
    <Select
      {...(!value && register)}
      {...baseProps}
      items={options}
      disabledKeys={options?.filter((o) => o.disabled).map((o) => o.value)}
      validationState={!!error ? 'invalid' : 'valid'}
      classNames={{
        trigger: 'bg-white border shadow-none',
        label: '!font-light uppercase text-xs',
        popoverContent: 'border w-auto border-themeBlack',
        value: 'text-themeBlack',
      }}
      selectedKeys={value ? (value instanceof Set ? value : [value]) : []}
      onChange={selectionMode === 'single' && (changeHandler || undefined)}
      onSelectionChange={
        selectionMode === 'multiple' ? changeHandler || undefined : undefined
      }
      onOpenChange={handleOpenSelect}
      selectionMode={selectionMode}
    >
      {options && (
        <SelectSection title={optionsLabel} className='w-auto'>
          {createSelectItems(options)}
        </SelectSection>
      )}
      {sectionOptions &&
        sectionOptions.map((section) => (
          <SelectSection
            key={section.sectionLabel}
            title={section.sectionLabel}
          >
            {createSelectItems(section.options)}
          </SelectSection>
        ))}
    </Select>
  );

  const input = (
    <div className={`${fullWidth ? 'w-full' : 'min-w-[160px]'} py-1`}>
      {controlledText && controlledText}
      {isArea && !controlledText && area}
      {(options || sectionOptions) && select}
      {!isArea && !options && !sectionOptions && !controlledText && inputComp}
      {children}
    </div>
  );
  if (!tooltip) return input;
  else
    return (
      // <Tooltip
      //   showArrow={true}
      //   content={tooltip}
      //   color='foreground'
      //   classNames={{ base: 'max-w-lg text-xs' }}
      //   isDismissable={false}
      // >
      // </Tooltip>
      <div
        className={`tooltip tooltip-info z-50 ${fullWidth ? 'w-full' : 'min-w-[160px]'
          }`}
        data-tip={tooltip}
      >
        {input}
      </div>
    );
};

export default InputFloatingLabel;
